import { useMemo } from 'react'
import { useQuery, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { toAppError, type AppError } from '../lib/errors'
import type { Row } from '../lib/db-types'

/**
 * Team hooks — Kondo-style single-team-per-user model.
 *
 * Every user has an auto-created personal team of 1 on signup, and can
 * optionally be in one shared team. There's no team switcher — tags and
 * folders are always user-scoped, and templates (snippets) are rendered
 * in two sections (Personal / Team) on the snippets page.
 *
 * Exposed:
 *   - `useTeams()`         — all teams the user is a member of
 *   - `usePersonalTeam()`  — the user's personal team (always exists)
 *   - `useSharedTeam()`    — their one non-personal team, or null
 *   - `useEnrichmentTeam()` — shared team when present, otherwise personal
 *
 * Removed:
 *   - `useCreateTeam()` (2026-05-16): a user's shared team is claimed by
 *     the post-signup trigger based on email domain. There is no
 *     explicit "create team" path anymore — webmail users don't get a
 *     shared team, corp users get one auto-claimed on first signup.
 *   - `useCurrentTeam` + `useSwitchTeam` (earlier iteration): we don't
 *     switch teams; the UI just shows both contexts.
 */

/**
 * Shape of a team row as it comes back from `public.teams`. We omit
 * `updated_at` from the select below for brevity — if callers ever need
 * it, add to the select list and it'll show up on this type automatically
 * on the next `npm run types:gen`.
 */
export type Team = Pick<
  Row<'teams'>,
  'id' | 'name' | 'slug' | 'plan_tier' | 'is_personal' | 'created_by' | 'created_at'
> & {
  // Added in 2026-05-16-domain-team-placement. Owner-controlled via
  // `set_team_auto_join`. True means corp-domain signups auto-join this
  // team; false means they land in the pending-requests queue.
  //
  // Not yet in the Row<'teams'> generated type (types:gen post-merge),
  // so declared here explicitly. Once regenerated the intersection is a
  // no-op. Nullable for the brief window before the backfill ran — in
  // practice all rows are non-null post-migration.
  auto_join_by_domain: boolean
}

export interface TeamMembership extends Team {
  // `role` is `text` in Postgres (CHECK constraint enforces the values),
  // so the generated type widens it to `string`. Narrow here so the UI
  // gets compile-time exhaustiveness on role checks.
  // Tiers: owner > admin > member. See 2026-05-15-role-hierarchy.sql.
  role: TeamRole
  joined_at: string
}

/**
 * Team membership tier. See 2026-05-15-role-hierarchy.sql for the capability
 * matrix. `admin` sits between `owner` and `member`: can invite/revoke/remove
 * members and triage join requests, but cannot reshape the hierarchy or
 * touch team_domains.
 */
export type TeamRole = 'owner' | 'admin' | 'member'

export const teamsKey = (userId: string | undefined): QueryKey =>
  ['teams', userId ?? 'anon']

/**
 * Lists every team the current user is a member of, with the user's role
 * on each.
 */
export function useTeams() {
  const { user } = useAuth()
  const key = teamsKey(user?.id)

  const query = useQuery<TeamMembership[]>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('team_members')
        .select(`
          role,
          joined_at,
          team:teams (
            id, name, slug, plan_tier, is_personal, created_by, created_at, auto_join_by_domain
          )
        `)
        .eq('user_id', user!.id)

      if (error) throw error

      return (data ?? [])
        .map((row) => {
          const t = row.team as unknown as Team | null
          if (!t) return null
          return {
            ...t,
            role: row.role as TeamRole,
            joined_at: row.joined_at as string,
          }
        })
        .filter((t): t is TeamMembership => t !== null)
    },
  })

  return {
    teams: query.data ?? [],
    loading: !!user && query.isPending,
    error: query.error ? toAppError(query.error) : null as AppError | null,
  }
}

/**
 * The user's personal team (auto-created on signup). Used when creating a
 * personal snippet — the snippet's `team_id` is set to this team's id.
 */
export function usePersonalTeam() {
  const { teams } = useTeams()
  return useMemo(() => teams.find((t) => t.is_personal) ?? null, [teams])
}

/**
 * The user's shared team, if they have one.
 *
 * Product assumption: a user is in ≤1 non-personal team. If we ever see
 * someone in multiple, we pick the first (most-recently joined). Worth
 * logging if we start tripping this in the wild, but zero-cost today.
 */
export function useSharedTeam() {
  const { teams } = useTeams()
  return useMemo(() => teams.find((t) => !t.is_personal) ?? null, [teams])
}

/**
 * Team scope used for server-side enrichment credentials/capabilities.
 *
 * Shared teams should use one provider key for the team. Solo users still need
 * enrichment, so their personal team is the private fallback. The Edge
 * Functions already authorize via team_members, and personal teams have the
 * user as owner from signup, so this keeps the security model unchanged.
 */
export function useEnrichmentTeam() {
  const { teams } = useTeams()
  return useMemo(
    () => teams.find((t) => !t.is_personal) ?? teams.find((t) => t.is_personal) ?? null,
    [teams],
  )
}

// `useCreateTeam` was removed in 2026-05-16 — see the header comment.
// Shared teams are created by the post-signup trigger.
